import type { components, operations } from '@connected/api-client/api.generated';

import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Carousel, CarouselContent, CarouselItem } from '@connected/ui/components/carousel';
import { Image } from '@connected/ui/components/image';
import { Separator } from '@connected/ui/components/separator';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@connected/ui/components/table';
import { useQuery } from '@tanstack/react-query';
import { Archive } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ComingSoonModal } from '@/components/coming-soon-modal';
import { Observation } from '@/components/observation';

export function CustomerReportContent({
    query,
    installation,
}: {
    query: operations['ViewElectricalProjectObservationPortalQuery_Get']['parameters']['query'];
    installation: components['schemas']['ViewElectricalProjectDistributionBoardPortalQueryResultBoard'] | components['schemas']['ViewElectricalProjectFinalCircuitPortalQueryResultCircuit'];
}) {
    const { t } = useTranslation('customer-module-electrical');
    const observationQuery = useQuery(portaalQueryOptions.electrical.observations.view(query));
    const [comingSoonModalOpen, setComingSoonModalOpen] = useState<boolean>(false);

    // const logbookData = [
    //     {
    //         name: 'Tomas Louwman',
    //         status: 'reported',
    //         maintenanceCompany: 'Kuijpers Installaties',
    //         description: 'Vervangingswerkzaamheden ingepland voor uitvoering.',
    //         date: '30-09-2024',
    //         files: [],
    //     },
    // ];

    // const columns: ColumnDef<ElektraNotificationData>[] = [
    //     {
    //         accessorKey: 'name',
    //         header: () => (
    //             <DataTable.Header>
    //                 {t('module-electrical.report.logbook-table.columns.name')}
    //             </DataTable.Header>
    //         ),
    //         cell: ({ getValue }) => {
    //             const name = getValue<string | undefined>();
    //             if (!name)
    //                 return null;

    //             return <DataTable.Cell variant="bold">{name}</DataTable.Cell>;
    //         },
    //     },
    //     {
    //         accessorKey: 'status',
    //         header: () => (
    //             <DataTable.Header>
    //                 {t('module-electrical.report.logbook-table.columns.status.title')}
    //             </DataTable.Header>
    //         ),
    //         cell: ({ getValue }) => {
    //             const status = getValue<string | undefined>();
    //             if (!status)
    //                 return null;

    //             let color = '#D92D20'; // reported
    //             switch (status) {
    //                 case 'planned':
    //                     color = '#175CD3';
    //                     break;
    //                 case 'execution':
    //                     color = '#FF9C66';
    //                     break;
    //                 case 'done':
    //                     color = '#16B364';
    //                     break;
    //             }

    //             return (
    //                 <DataTable.Cell variant="colorLabel" color={color}>
    //                     {t(`notifications.logbook-table.columns.status.${status}`)}
    //                 </DataTable.Cell>
    //             );
    //         },
    //     },
    //     {
    //         accessorKey: 'maintenanceCompany',
    //         header: () => (
    //             <DataTable.Header>
    //                 {t('module-electrical.report.logbook-table.columns.maintenance-company')}
    //             </DataTable.Header>
    //         ),
    //         cell: ({ getValue }) => {
    //             const maintenanceCompany = getValue<string | undefined>();
    //             if (!maintenanceCompany)
    //                 return null;

    //             return <DataTable.Cell>{maintenanceCompany}</DataTable.Cell>;
    //         },
    //     },
    //     {
    //         accessorKey: 'description',
    //         header: () => (
    //             <DataTable.Header>
    //                 {t('module-electrical.report.logbook-table.columns.description')}
    //             </DataTable.Header>
    //         ),
    //         cell: ({ getValue }) => {
    //             const description = getValue<string | undefined>();
    //             if (!description)
    //                 return null;

    //             return <DataTable.Cell>{description}</DataTable.Cell>;
    //         },
    //     },
    //     {
    //         accessorKey: 'date',
    //         header: ({ column }) => (
    //             <DataTable.Header variant="sort" column={column} default="asc">
    //                 {t('module-electrical.report.logbook-table.columns.date')}
    //             </DataTable.Header>
    //         ),
    //         cell: ({ getValue }) => {
    //             const date = getValue<string | undefined>();
    //             if (!date)
    //                 return null;

    //             return <DataTable.Cell>{date}</DataTable.Cell>;
    //         },
    //     },
    //     {
    //         accessorKey: 'files',
    //         header: () => <DataTable.Header />,
    //         cell: ({ getValue }) => {
    //             const files = getValue<string | undefined>();
    //             if (!files)
    //                 return null;

    //             return (
    //                 <>
    //                     {files.length
    //                         ? (
    //                             <DataTable.Cell variant="files" files={[{}]} />
    //                         )
    //                         : (
    //                             ''
    //                         )}
    //                 </>
    //             );
    //         },
    //     },
    // ];

    return (
        <>
            <div className="flex flex-col sm:flex-row justify-between items-center">
                <div className="flex flex-col max-sm:flex-wrap gap-1 justify-start">
                    <h1 className="text-2xl font-semibold">
                        {`${t('module-electrical.report.observations.title')} ${t('module-electrical.report.on')} ${installation?.Identifier}`}
                    </h1>
                    <h2 className="text-base font-medium text-text-primary/70">
                        {observationQuery.data?.Observation?.LocationRemark}
                    </h2>
                </div>
                <div className="flex gap-4">
                    <Button
                        variant="link"
                        className="flex items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background"
                        onClick={() => setComingSoonModalOpen(true)}
                    >
                        <Archive className="w-5 h-5" />
                        {t('module-electrical.report.buttons.archive')}
                    </Button>
                    <ComingSoonModal open={comingSoonModalOpen} onOpenChange={setComingSoonModalOpen} />
                </div>
            </div>
            <Separator className="my-5" />
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/4">
                    <span className="font-semibold">
                        {t('module-electrical.report.photos-table.title.observation')}
                    </span>
                </div>
                <div className="w-full sm:w-3/4">
                    {observationQuery.data?.Observation?.Files && observationQuery.data?.Observation?.Files.length > 0
                        ? (
                                <Carousel loop>
                                    <CarouselContent className="-ml-2 md:-ml-4">
                                        {observationQuery.data.Observation.Files.map((file, idx) => (
                                            <CarouselItem className="pl-2 md:pl-4 basis-1/1 sm:basis-1/2 lg:basis-1/3 xl:basis-1/4" key={file.ElectricalProjectFileId ?? idx}>
                                                {file.File && (
                                                    <Image
                                                        previewer
                                                        className="h-52 w-auto object-contain rounded-lg"
                                                        alt={file?.File?.Name ?? ''}
                                                        queryOptions={portaalQueryOptions.files.electricalProject({
                                                            ElectricalProjectFileId: file.ElectricalProjectFileId!,
                                                            ImageQuality: 'OriginalWebp',
                                                        })}
                                                        enabled={!!file.ElectricalProjectFileId}
                                                    />
                                                )}
                                            </CarouselItem>
                                        ))}
                                    </CarouselContent>
                                </Carousel>
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noImagesFound')}</p>
                                </div>
                            )}
                </div>
            </div>
            <Separator className="my-5" />
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="sm:w-1/4 w-full">
                    <span className="font-semibold">
                        {t('module-electrical.report.observations.details-table.header.title')}
                    </span>
                </div>
                <div className="sm:w-3/4 w-full rounded-2xl border ">
                    <Table className="overflow-hidden rounded-2xl">
                        <TableHeader className="bg-background-secondary">
                            <TableRow className="h-8">
                                <TableHead className="text-text-primary/70 font-semibold w-1/2">
                                    {t('module-electrical.report.observations.details-table.header.name')}
                                </TableHead>
                                <TableHead className="text-text-primary/70 font-semibold w-1/2">
                                    {t('module-electrical.report.observations.details-table.header.value')}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow className="h-16">
                                <TableCell className="font-semibold">
                                    {t('module-electrical.report.observations.details-table.columns.observation')}
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-1">
                                        {/* TODO: because of scale, the scale of tooltip is scaled down aswell */}
                                        {/* TODO: needs to be dynamic color for type of observation */}
                                        <Observation
                                            color={observationQuery.data?.Observation?.HexColorCode ?? ''}
                                            label={t(
                                                'module-electrical.report.observations.details-table.columns.observation',
                                            )}
                                            className="scale-75"
                                            children={[]}
                                        />
                                        {t(`module-electrical.report.code-kinds.${observationQuery.data?.Observation?.CodeKind?.toLowerCase()}`)}
                                    </div>
                                </TableCell>
                            </TableRow>
                            <TableRow className="h-16">
                                <TableCell className="font-semibold">
                                    {t('module-electrical.report.observations.details-table.columns.date')}
                                </TableCell>
                                <TableCell>
                                    {observationQuery.data?.Observation?.Date
                                        ? new Date(observationQuery.data.Observation.Date).toLocaleDateString('nl-NL')
                                        : ''}
                                </TableCell>
                            </TableRow>
                            <TableRow className="h-16">
                                <TableCell className="font-semibold">
                                    {t('module-electrical.report.observations.details-table.columns.location')}
                                </TableCell>
                                <TableCell>{observationQuery.data?.Observation?.LocationRemark}</TableCell>
                            </TableRow>
                            <TableRow className="h-16">
                                <TableCell className="font-semibold">
                                    {t('module-electrical.report.observations.details-table.columns.classification-group')}
                                </TableCell>
                                <TableCell>
                                    {observationQuery.data?.Observation?.Group?.length
                                        ? (
                                                <>
                                                    {observationQuery.data?.Observation?.Group}
                                                    {' '}
                                                    (
                                                    {t(`module-electrical.report.code-kinds.${observationQuery.data?.Observation?.CodeKind?.toLowerCase()}`).toLowerCase()}
                                                    )
                                                </>
                                            )
                                        : (
                                                <div>
                                                    {t('module-electrical.report.code-kinds.undefined')}
                                                </div>
                                            )}
                                </TableCell>
                            </TableRow>
                            <TableRow className="h-16">
                                <TableCell className="font-semibold">
                                    {t('module-electrical.report.observations.details-table.columns.remark')}
                                </TableCell>
                                <TableCell>{observationQuery.data?.Observation?.Remark}</TableCell>
                            </TableRow>
                            <TableRow className="h-16">
                                <TableCell className="font-semibold">
                                    {t('module-electrical.report.observations.details-table.columns.recommendation')}
                                </TableCell>
                                <TableCell>{observationQuery.data?.Observation?.Recommendation}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>
            {/* <Separator className="my-5" />
            <div>
                <DataTable.TableHead
                    title={t('module-electrical.report.logbook-table.header.title')}
                    subtitle={t('module-electrical.report.logbook-table.header.subtitle')}
                    buttons={[
                        {
                            name: t('module-electrical.report.logbook-table.buttons.add'),
                            icon: <Plus className="w-5 h-5" />,
                            callback: () => console.log('Added'),
                        },
                    ]}
                />
                <DataTable columns={columns} data={logbookData} />
                {/* TODO: implement DataTable for files
            </div> */}
        </>
    );
}
